import React, { useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Badge } from '../components/ui/badge';
import { DiscoverCarsWidget } from '../components/DiscoverCarsWidget';
import { AffiliateDisclosure } from '../components/AffiliateDisclosure';
import { Car, Route, BadgeCheck, Fuel, KeyRound, MapPinned, ShieldCheck, Wallet } from 'lucide-react';

const PERKS = [
  { icon: Wallet, title: 'No hidden fees', desc: 'Transparent, all-in pricing with taxes shown upfront' },
  { icon: BadgeCheck, title: 'Free cancellation', desc: 'Most bookings cancel free up to 48h before pickup' },
  { icon: Route, title: 'Unlimited mileage', desc: 'Drive as far as you like on eligible cars' },
  { icon: ShieldCheck, title: 'Full insurance options', desc: 'Add cover for total peace of mind' },
];

const CATEGORIES = [
  { icon: Car, label: 'Economy', note: 'City-friendly & fuel-efficient' },
  { icon: Car, label: 'SUV', note: 'Space for family & luggage' },
  { icon: Car, label: 'Luxury', note: 'Premium drives for special trips' },
  { icon: Car, label: 'Vans', note: 'Groups & road trips' },
];

const HOW = [
  { icon: MapPinned, step: '1', title: 'Pick location & dates', desc: 'Search airports and city pickup points worldwide.' },
  { icon: KeyRound, step: '2', title: 'Compare & choose', desc: 'Filter by price, car type, transmission & supplier.' },
  { icon: Fuel, step: '3', title: 'Book & hit the road', desc: 'Confirm on the partner site and collect your keys.' },
];

export default function CarRentals() {
  useEffect(() => { document.title = 'Self-Drive Car Rentals | FlightDeals.in'; }, []);
  return (
    <Layout>
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 h-72">
          <img src="https://images.unsplash.com/photo-1502877338535-766e1452684a?w=1600&q=80&auto=format&fit=crop" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-8">
          <Badge className="rounded-full bg-accent/15 text-accent mb-2"><Car className="h-3.5 w-3.5 mr-1" />Self-drive rentals</Badge>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-primary-foreground">Rent a car, drive on your terms</h1>
          <p className="text-primary-foreground/80 mt-1 max-w-2xl">Compare self-drive car hire across trusted suppliers — economy to luxury, at airports and cities worldwide. Book in minutes with free cancellation on most cars.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Perks */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          {PERKS.map((p) => (
            <div key={p.title} className="rounded-2xl border border-border bg-card p-4" data-testid={`car-perk-${p.title.split(' ')[0].toLowerCase()}`}>
              <span className="grid place-items-center h-10 w-10 rounded-xl bg-accent/10 text-accent mb-2"><p.icon className="h-5 w-5" /></span>
              <p className="font-semibold text-sm">{p.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{p.desc}</p>
            </div>
          ))}
        </div>

        <div className="mb-4"><AffiliateDisclosure variant="banner" /></div>

        {/* Search widget */}
        <div className="rounded-2xl border border-border bg-card p-4" data-testid="car-widget-wrap">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Search rental cars</p>
          <DiscoverCarsWidget testid="car-widget" />
        </div>

        {/* Categories */}
        <div className="mt-10">
          <h2 className="font-display text-2xl font-semibold mb-1">Find the right car</h2>
          <p className="text-muted-foreground mb-4">From nippy city cars to spacious SUVs — pick what fits your trip.</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {CATEGORIES.map((c) => (
              <div key={c.label} className="rounded-2xl border border-border bg-card p-5 card-elevate" data-testid={`car-cat-${c.label.toLowerCase()}`}>
                <c.icon className="h-6 w-6 text-accent mb-2" />
                <p className="font-semibold">{c.label}</p>
                <p className="text-sm text-muted-foreground mt-0.5">{c.note}</p>
              </div>
            ))}
          </div>
        </div>

        {/* How it works */}
        <div className="mt-10 rounded-3xl border border-border bg-gradient-to-br from-accent/10 to-transparent p-6 sm:p-8">
          <h2 className="font-display text-2xl font-semibold mb-5">How it works</h2>
          <div className="grid gap-5 md:grid-cols-3">
            {HOW.map((h) => (
              <div key={h.step} className="flex items-start gap-3">
                <span className="grid place-items-center h-10 w-10 rounded-xl bg-accent text-accent-foreground font-semibold shrink-0">{h.step}</span>
                <div>
                  <p className="font-semibold flex items-center gap-1.5"><h.icon className="h-4 w-4 text-accent" />{h.title}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">{h.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
